import type { FC, ReactNode } from 'react'

import { createElement, useRef } from 'react'
import {
    gsap,
    SplitText,
    useGSAP,
    MOTION_QUERY
} from '@/lib/gsap'

interface AnimatedHeadingProps {
    as?: 'h1' | 'h2' | 'h3' | 'p'
    children: ReactNode
    className?: string
    delay?: number
    immediate?: boolean
    id?: string
}

export const AnimatedHeading: FC<AnimatedHeadingProps> = ({
    as = 'h2',
    children,
    className,
    delay = 0,
    immediate = false,
    id
}): ReactNode => {
    const ref = useRef<HTMLElement>(null)

    useGSAP(() => {
        const element = ref.current

        if (!element) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            let played = false

            const split = SplitText.create(element, {
                type: 'lines',
                mask: 'lines',
                linesClass: 'split-line',
                autoSplit: true,
                onSplit: self => {
                    if (played) return

                    return gsap.from(self.lines, {
                        yPercent: 125,
                        duration: 1.25,
                        ease: 'expo.out',
                        stagger: 0.1,
                        delay,
                        scrollTrigger: immediate ? undefined : { trigger: element, start: 'top 88%', once: true },
                        onStart: () => {
                            played = true
                        }
                    })
                }
            })

            return () => split.revert()
        })

        return () => media.revert()
    }, { scope: ref })

    return createElement(as, { ref, className, id }, children)
}
